
import { Component, Vue } from 'vue-property-decorator'
import { Getter } from "vuex-class";
import html2canvas from 'html2canvas'
import { Toast } from 'vant'
import ActivityPoster from './ActivityPoster.vue'
import ProductPoster from './ProductPoster.vue'
import ActivityShareCard from './ActivityShareCard.vue'
import ProductShareCard from './ProductShareCard.vue'
import PosterPreview from './PosterPreview.vue'
import { shareMiniProgram } from '@/utils/h5ToApp'
import { getShareQrCode } from '@/api/share'
import { handleFill } from '@/utils/util'

/** 分享参数 */
export interface SharePopupParams {
  /** 分享类型：activity-活动，product-商品 */
  type: 'activity' | 'product'
  /** 接龙活动ID */
  joinActivityId: string
  /** /商品ID */
  goodsId?: string
  /** 活动标题或商品名称 */
  title: string
  /** 图片数组（活动用多张，商品用一张） */
  images: string[]
  /** 价格展示文本，如 2.8-120 或 75 */
  price: string
  /** 价格展示单位，如 kg */
  unit?: string
  /** 划线价展示文本，如 10 */
  linePrice?: string
  /** 店铺名称 */
  storeName: string
  /** 店铺logo地址 */
  storeLogo: string
  /** 小程序分享二维码（后端返回） */
  qrCode?: string
  /** 发布时间 */
  createTime?: string
  /** 到货时间 */
  arrivalTime?: string
}

@Component({
  components: {
    ActivityPoster,
    ProductPoster,
    ActivityShareCard,
    ProductShareCard,
    PosterPreview,
  },
})
class SharePopup extends Vue {
  @Getter companyId!: string;
  handleFill = handleFill

  // 分享弹窗显隐
  visible = false
  // 海报预览层显隐
  posterPreviewVisible = false
  // 保存 loading
  saveLoading = false

  // 业务参数
  popupParams: SharePopupParams | null = null
  // 海报参数（复制一份避免响应式干扰截图）
  posterParams: SharePopupParams | null = null
  // 卡片参数（用于分享给好友时生成卡片背景图）
  cardParams: SharePopupParams | null = null
  // 生成的海报图片地址
  posterUrl = ''
  // 生成的分享卡片图片地址（用于预览层展示）
  shareCardUrl = ''

  // 是否接龙活动
  get isActivity () {
    const { type = 'activity' } = this.popupParams
    return type === 'activity'
  }

  // 小程序分享页面路径
  get miniProgramSharePath () {
    const { joinActivityId = '', goodsId = '' } = this.popupParams
    return this.isActivity
      ? `/pages-shop/jielong-detail/index?joinActivityId=${joinActivityId}&isShare=1`
      : `/pages-shop/goods-detail?joinActivityId=${joinActivityId}&goodsId=${goodsId}&type=fromJieLong&isShare=1`
  }

  /**
   * 将远程图片URL转换为相对路径（通过代理访问，避免CORS跨域）
   * @param url 原始图片URL
   * @returns 转换后的相对路径
   */
  convertImageUrl (url: string): string {
    if (!url) return url
    const sftpUrl = this.$store.state.user.sftpUrl
    const remoteHosts = [sftpUrl]
    for (const host of remoteHosts) {
      if (url.startsWith(host)) {
        return url.replace(host, '')
      }
    }
    return url
  }

  /**
   * 转换所有图片URL为相对路径
   * @param params 原始参数
   * @returns 转换后的参数
   */
  convertParamsImages (params: SharePopupParams): SharePopupParams {
    return {
      ...params,
      images: params.images.map(img => this.convertImageUrl(img)),
      storeLogo: this.convertImageUrl(params.storeLogo),
    }
  }

  /**
   * 将图片URL转为base64（利用代理同源优势，彻底避免CORS）
   * @param url 图片URL
   * @param timeout 超时时间（毫秒）
   * @returns base64字符串，失败则返回原始URL
   */
  async fetchImageAsBase64 (url: string, timeout = 8000): Promise<string> {
    if (!url) return ''
    // 已经是 base64 / data URI 直接返回
    if (url.startsWith('data:')) return url

    try {
      const controller = new AbortController()
      const timer = setTimeout(() => controller.abort(), timeout)

      const response = await fetch(url, { signal: controller.signal })
      clearTimeout(timer)

      if (!response.ok) return url

      const blob = await response.blob()
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = () => resolve(url)
        reader.readAsDataURL(blob)
      })
    } catch {
      // fetch 失败则返回原始URL，让 html2canvas 尝试直接加载
      return url
    }
  }

  /**
   * 批量将图片URL转为base64
   */
  async fetchImagesAsBase64 (urls: string[]): Promise<string[]> {
    return Promise.all(urls.map(url => this.fetchImageAsBase64(url)))
  }

  /**
   * 显示弹窗
   * @param params 业务参数
   */
  async showPopup (params: SharePopupParams) {
    // 参数校验失败时不打开弹窗
    if (!params) {
      Toast('分享参数为空')
      return
    }
    const toast = Toast.loading({
      message: '加载中',
      forbidClick: true,
      duration: 0
    })

    try {
      // 转换远程图片URL为相对路径（代理）
      const converted = this.convertParamsImages(params)

      // 将所有图片转为 base64，彻底避免 html2canvas 跨域问题
      this.popupParams = {
        ...converted,
        images: await this.fetchImagesAsBase64(converted.images),
        storeLogo: await this.fetchImageAsBase64(converted.storeLogo)
      }

      await this.onGenerateCard()
      this.visible = true
    } finally {
      toast.clear()
    }
  }

  /** 关闭弹窗（仅右上角关闭按钮等主动关闭） */
  closePopup () {
    this.visible = false
  }

  /** 弹窗关闭动画结束后清理 */
  onPopupClosed () {
    this.shareCardUrl = ''
    this.cardParams = null
    this.$emit('close')
  }

  // 生成分享卡片
  async onGenerateCard () {
    try {
      // 设置卡片数据，触发DOM渲染（图片已经是 base64，无需预加载）
      this.cardParams = { ...this.popupParams }
      await this.$nextTick()
      // 给 van-image 组件时间渲染内部 <img> 标签
      await this.wait(300)

      // 两阶段检测：先等 img 出现，再等 img 加载完成
      const cardWrap = this.$refs.cardWrapRef as HTMLElement
      if (cardWrap) {
        await this.waitForImagesLoaded(cardWrap)
      }

      // 生成卡片背景图
      const cardUrl = await this.captureElement(cardWrap, 2)
      if (!cardUrl) {
        Toast('卡片生成失败')
        return
      }

      this.shareCardUrl = cardUrl
      await this.$nextTick()
      this.cardParams = null
    } catch (error) {
      Toast('分享卡片生成失败')
    }
  }

  /** 点击生成海报 */
  async onGeneratePoster () {
    this.closePopup()

    const toast = Toast.loading({
      message: '加载中',
      forbidClick: true,
      duration: 0,
    })

    try {
      let qrCode = ''

      try {
        const { joinActivityId = '', goodsId = '' } = this.popupParams
        const { data } = await getShareQrCode({
          storeId: this.companyId,
          foreignKeyId: this.isActivity ? joinActivityId : goodsId,
          foreignType: this.isActivity ? 1 : 2, // 1-接龙活动, 2-接龙商品
          shareUrl: this.miniProgramSharePath
        })
        qrCode = 'data:image/png;base64,' + data
      } catch (error) {
        console.warn('[SharePopup] 获取小程序码失败，使用默认占位图:', error)
      }

      // 设置海报数据，触发DOM渲染（图片已经是 base64，无需预加载）
      this.posterParams = {
        ...this.popupParams,
        qrCode,
      }
      await this.$nextTick()
      // 给 van-image 组件时间渲染内部 <img> 标签
      await this.wait(300)

      // 两阶段检测：先等 img 出现，再等 img 加载完成
      const posterWrap = this.$refs.posterWrapRef as HTMLElement
      if (posterWrap) {
        await this.waitForImagesLoaded(posterWrap)
      }

      const posterUrl = await this.captureElement(posterWrap, 3)
      if (posterUrl) {
        this.posterUrl = posterUrl
        this.posterPreviewVisible = true
      } else {
        Toast('海报生成失败')
      }
    } catch (error) {
      Toast('海报生成失败')
    } finally {
      toast.clear()
      this.posterParams = null
    }
  }

  /**
   * 等待元素内所有图片加载完成（两阶段检测）
   * 阶段1：等待 van-image 异步渲染出 <img> 标签到 DOM
   * 阶段2：等待所有 <img> 真正加载完成（complete + naturalWidth > 0）
   * @param element 目标元素
   * @param maxWait 最大等待时间（毫秒）
   */
  async waitForImagesLoaded (element: HTMLElement, maxWait = 8000): Promise<void> {
    const startTime = Date.now()
    const checkInterval = 100

    return new Promise((resolve) => {
      const check = () => {
        const elapsed = Date.now() - startTime
        const images = element.querySelectorAll('img')

        // 阶段1：还没有 img 标签（van-image 尚未渲染内部 img），继续等待
        if (images.length === 0) {
          if (elapsed >= maxWait) {
            console.warn('[SharePopup] 等待超时，未检测到 img 标签')
            resolve()
            return
          }
          setTimeout(check, checkInterval)
          return
        }

        // 阶段2：检查所有 img 是否加载完成
        let allDone = true
        images.forEach((img) => {
          // complete 为 false 说明还在加载
          // complete 为 true 但 naturalWidth 为 0 说明加载失败
          if (!img.complete || img.naturalWidth === 0) {
            allDone = false
          }
        })

        if (allDone || elapsed >= maxWait) {
          if (!allDone) {
            const failedCount = Array.from(images).filter(img => !img.complete || img.naturalWidth === 0).length
            console.warn(`[SharePopup] 等待超时，${failedCount}/${images.length} 张图片未加载完成`)
          }
          resolve()
          return
        }

        setTimeout(check, checkInterval)
      }

      check()
    })
  }

  /**
   * 等待指定时间，确保DOM和图片完全渲染
   * @param ms 等待时间（毫秒）
   */
  wait (ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  /**
   * 公共截图方法：使用 html2canvas 将指定 DOM 元素转为 base64 图片
   * @param element 要截图的 DOM 元素
   * @param scale 截图清晰度倍数
   */
  async captureElement (element: HTMLElement | undefined, scale: number): Promise<string | null> {
    if (!element) {
      Toast('截图元素未找到')
      return null
    }

    try {
      const canvas = await html2canvas(element, {
        backgroundColor: '#ffffff',
        scale: scale,
        useCORS: true,
        logging: false,
        allowTaint: false,
        removeContainer: true,
        onclone: (clonedDoc) => {
          // html2canvas 不支持 object-fit 属性，需要手动转换为 background-image
          const imgs = clonedDoc.querySelectorAll('img')
          imgs.forEach((img) => {
            const style = img.style
            if (style.objectFit === 'cover') {
              const parent = img.parentElement
              if (!parent) return
              // 将 img 的 src 转移到父元素的 background-image
              const src = img.src || img.getAttribute('src')
              if (src) {
                parent.style.backgroundImage = `url("${src}")`
                parent.style.backgroundSize = 'cover'
                parent.style.backgroundPosition = 'center'
                parent.style.backgroundRepeat = 'no-repeat'
              }
              // 隐藏原 img
              img.style.display = 'none'
            }
          })
        },
      })

      return canvas.toDataURL('image/png', 1.0)
    } catch (error) {
      return null
    }
  }

  /** 关闭海报预览 */
  closePosterPreview () {
    this.posterPreviewVisible = false
    this.posterUrl = ''
    this.posterParams = null
  }

  /** 分享预览层-确认分享给好友 */
  onShareToFriendConfirm () {
    const params = this.popupParams
    if (!params) return
    if (!this.shareCardUrl) {
      Toast('分享卡片为空')
      return
    }

    // 分享数据
    const shareData: any = {
      // 小程序页面路径
      path: this.miniProgramSharePath,
      title: params.title,
      thumbBase64: this.shareCardUrl,
      miniProgramType: 'release'
    }

    shareMiniProgram(shareData)
  }

  /** 分享预览层-切换到生成海报 */
  onGeneratePosterFromPreview () {
    this.onGeneratePoster()
  }
}

export default SharePopup
